/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { compose } from 'recompose';
import { connect, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { object, string, bool } from 'prop-types';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import { getFeatureFlag } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import ScrollbarContainer from '../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainer';

const useStyles = makeStyles((theme) => ({
    contentTop: {
        // Desktop
        margin: '0px auto',
        maxWidth: '1380px',
        color: theme.palette.black,
        // Tablet
        [theme.breakpoints.down('md')]: {
            margin: '20px 20px 30px',
        },
        // Mobile
        [theme.breakpoints.up('sm')]: {
            display: 'flex',
        },
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 10px 0',
        },
    },
    contentLeft: {
        width: '100%',
        // Tablet
        [theme.breakpoints.down('md')]: {
            flex: '1 1 auto',
            display: 'flex',
            maxWidth: '100%',
            marginRight: '20px',
            flexDirection: 'column',
        },
        // Mobile
        [theme.breakpoints.down(600)]: {
            maxWidth: '100%',
        },
        marginRight: '20px',
        maxWidth: '665px',
    },
    contentRight: {
        width: '100%',
        maxWidth: '980px',
        position: 'relative',
        '& .MuiFormControlLabel-root': {
            marginLeft: 0,
        },
        // Tablet
        [theme.breakpoints.down('md')]: {
            width: 'calc(60% - 20px)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100%',
        },
        [theme.breakpoints.down('600')]: {
            padding: '10px 15px',
        },
    },
}));

// documentation: https://1800flowersinc.atlassian.net/wiki/spaces/RP/pages/288227337/PDP+Product+Details+Page
const TwoColRedesignResponsive = (props) => {
    const {
        ssrDeviceType,
        components,
        isPassportAfterCta,
        isPdpPassportHidden,
    } = props;

    const {
        ProductImageUI,
        SmartGift,
        ProductTitle,
        ProductReview,
        GraphqlProductSkus,
        GraphqlAddonContainer,
        SnipeBanner,
        GraphqlAgeVerification,
        SubscriptionProductBannerUI,
        ProductQuantity,
        GraphqlPDPPromoContainerTop,
        GraphqlPDPPromoContainer,
        PmallSummary,
        PmallPersonalization,
        PassportBannerUI,
        PDPAddToCart,
        NBCUMovieBanner,
        PDPDeliveryPopupContainer,
        GoDeluxe,
        ProductDetails,
        GCIPersonalization,
        PromoMessageUI,
        PLAProductIcon,
        QandASection,
    } = components;

    const classes = useStyles();

    // PowerReviews Flags
    const isPDPReviewsEnabled = useSelector(getFeatureFlag(['is-pdp-reviews-enabled']));
    const isPowerReviewEnabled = useSelector(getFeatureFlag(['is-power-review-enabled']));

    // uncomment when needed
    // const variantName = 'TwoColRedesignResponsive'; // this should be unique and never change

    const desktopLayout = () => (
        <div
            className={classes.contentTop}
            data-testid="pdp-variant-twocolredesignresponsive"
        >
            <ScrollbarContainer
                contentContainerId="contentRight"
                isFoodBrand
                type="productBase"
            >
                <div className={classes.contentLeft}>
                    {ProductImageUI()}
                    {SmartGift()}
                </div>
            </ScrollbarContainer>
            <div className={classes.contentRight} id="contentRight">
                {ProductTitle()}
                {isPDPReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                {GraphqlPDPPromoContainerTop()}
                {GraphqlProductSkus()}
                {GraphqlAddonContainer()}
                {GCIPersonalization()}
                {SnipeBanner()}
                {GraphqlAgeVerification()}
                {SubscriptionProductBannerUI()}
                {PmallSummary()}
                {GraphqlPDPPromoContainer()}
                {PmallPersonalization()}
                {!isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                {ProductQuantity()}
                {PDPAddToCart()}
                {isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                {NBCUMovieBanner()}
                {PDPDeliveryPopupContainer()}
                {PromoMessageUI()}
                {ProductDetails()}
                {QandASection()}
            </div>
        </div>
    );

    const mobileLayout = () => (
        <>
            {PLAProductIcon()}
            <div className={classes.contentTop}>
                <div className={classes.contentLeft}>
                    {ProductImageUI()}
                </div>
                <div className={classes.contentRight} id="contentRight">
                    {ProductTitle()}
                    {isPDPReviewsEnabled && isPowerReviewEnabled && ProductReview()}
                    {GraphqlPDPPromoContainerTop()}
                    {GraphqlProductSkus()}
                    {GoDeluxe(null, true)}
                    {GraphqlAddonContainer()}
                    {GCIPersonalization()}
                    {SnipeBanner()}
                    {GraphqlAgeVerification()}
                    {SubscriptionProductBannerUI()}
                    {GraphqlPDPPromoContainer()}
                    {PmallSummary()}
                    {PmallPersonalization()}
                    {!isPassportAfterCta && !isPdpPassportHidden &&  PassportBannerUI()}
                    {ProductQuantity()}
                    {PDPAddToCart()}
                    {isPassportAfterCta && !isPdpPassportHidden && PassportBannerUI()}
                    {NBCUMovieBanner()}
                    {PDPDeliveryPopupContainer()}
                    {PromoMessageUI()}
                    {SmartGift()}
                    {GoDeluxe()}
                    {ProductDetails()}
                    {QandASection()}
                </div>
            </div>
        </>
    );

    if (ssrDeviceType === 'mobile') {
        return mobileLayout();
    }

    return desktopLayout();
};

const mapStateToProps = (state) => ({
    ssrDeviceType: getSSRDeviceType(state),
});

TwoColRedesignResponsive.propTypes = {
    ssrDeviceType: string.isRequired,
    components: object.isRequired,
    isPassportAfterCta: bool,
    isPdpPassportHidden: bool,
};

TwoColRedesignResponsive.defaultProps = {
    isPassportAfterCta: false,
    isPdpPassportHidden: false,
};

const enhance = compose(
    connect(mapStateToProps),
);

export default enhance(TwoColRedesignResponsive);
